import { axiosArisChatInstance } from "../AxiosConfiguration";

/**
 * Journey read APIs — aris-ai, `/ARIS/v4/journey/*` (guide §5, WP-W10).
 *
 * The screens moved off core-lambda's `/api/v4/journey/*` (now thin relays,
 * then deleted). Same payloads, key for key. A success is a raw object; an
 * error still carries `{status, status_code, message, payload}`, except a 401,
 * which is FastAPI's `{detail}` — so branch on status, not body shape.
 *
 * Home-screen sections degrade to null independently: a null section means
 * "hide the card", never "error the screen".
 */

const unwrap = (res) => {
  const data = res?.data;
  if (data && typeof data === "object") return data.payload ?? data;
  return data;
};

/** Momentum v2 (guide §5.1c): three dimensions, always in this order. */
export const MOMENTUM_DIMENSION = {
  HEALTHIER: "HEALTHIER",
  MORE_CONNECTED: "MORE_CONNECTED",
  MORE_FULFILLED: "MORE_FULFILLED",
};

/** `NOT_ENOUGH_DATA` is internal: render the dimension's `why` instead. */
export const MOMENTUM_NOT_ENOUGH_DATA = "NOT_ENOUGH_DATA";

/** The four-state per-discovery status (contract §4.1). */
export const DISCOVERY_STATUS = {
  COMPLETED: "COMPLETED",
  AVAILABLE: "AVAILABLE",
  LOCKED: "LOCKED",
  EMERGES: "EMERGES",
};

/**
 * `GET /ARIS/v4/journey/home-screen` (v26.5, guide §5.1): mission,
 * what_matters_most, whats_next, todays_check_in, momentum (v2),
 * mission_alignment, your_progress, activation_state.
 */
export const getJourneyHome = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/journey/home-screen");
  return unwrap(res);
};

/** `GET /ARIS/v4/journey/discovery-progress` — the five Discoveries with
 * per-item `domain` (canonical) and four-state `status`. `resolved: false`
 * means the read failed: render a retry, never an empty journey. */
export const getDiscoveryProgress = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/journey/discovery-progress");
  return unwrap(res);
};

/** `GET /ARIS/v4/journey/mission` — mission, priorities, alignment. */
export const getMissionDetails = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/journey/mission");
  return unwrap(res);
};

/** `GET /ARIS/v4/journey/insight/{id}/understanding` — "Understanding the Pattern". */
export const getInsightUnderstanding = async (insightId) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/journey/insight/${encodeURIComponent(insightId)}/understanding`
  );
  return unwrap(res);
};

/** `GET /ARIS/v4/journey/insight/{id}/detail` — the pattern with resolved evidence. */
export const getInsightDetail = async (insightId) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/journey/insight/${encodeURIComponent(insightId)}/detail`
  );
  return unwrap(res);
};

export const confirmInsight = async (insightId) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/journey/insight/${encodeURIComponent(insightId)}/confirm`
  );
  return unwrap(res);
};

export const dismissInsight = async (insightId) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/journey/insight/${encodeURIComponent(insightId)}/dismiss`
  );
  return unwrap(res);
};

/**
 * `GET /ARIS/v4/journey/v2/momentum` — the standalone read of the same
 * three-dimension payload the home screen carries under `momentum`.
 */
export const getMomentum = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/journey/v2/momentum");
  return unwrap(res);
};

/** `GET /ARIS/v4/journey/recent` — Your Progress, newest first. */
export const getRecentJourney = async (limit = 20) => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/journey/recent", {
    params: { limit },
  });
  return unwrap(res);
};

/**
 * `GET /ARIS/v4/journey/commitments?state=&category=&limit=&skip=` →
 * `{commitments[], total, limit, skip}`, newest first (WP-W11). There is no
 * home-screen section for commitments; the home card calls this with
 * `limit: 5` alongside home-screen.
 */
export const getCommitments = async (params = {}) => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/journey/commitments", { params });
  return unwrap(res);
};

/** `GET /ARIS/v4/journey/commitments/{id}` → `{commitment}`. Someone else's is 404. */
export const getCommitment = async (commitmentId) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/journey/commitments/${encodeURIComponent(commitmentId)}`
  );
  return unwrap(res);
};

/** The moves a member can make on a commitment. An illegal one is a 409. */
export const COMMITMENT_ACTION = {
  COMPLETE: "complete",
  DELAY: "delay",
  RELEASE: "release",
};

/** `POST /ARIS/v4/journey/commitments/{id}/{complete|delay|release}` → `{commitment}`. */
export const postCommitmentAction = async (commitmentId, action, body = {}) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/journey/commitments/${encodeURIComponent(commitmentId)}/${action}`,
    body
  );
  return unwrap(res);
};

/* ------------------------------------------------------ Mission evolution */

/**
 * Mission Evolution (Journey v26.3 addendum, aris-ai plan 41b). Every route is
 * owner-scoped by the token: an id that belongs to someone else is a 404.
 * Errors carry `{status: "failed", status_code, message, payload: {code}}`;
 * read the code with `missionErrorCode`. Only `confirmMissionDraft` changes
 * the Mission — leaving, closing or discarding never does.
 */

/** `payload.code` values the screens branch on. */
export const MISSION_ERROR = {
  NO_ACTIVE_MISSION: "NO_ACTIVE_MISSION",
  UNKNOWN_DRAFT: "UNKNOWN_DRAFT",
  UNKNOWN_EVOLUTION_SESSION: "UNKNOWN_EVOLUTION_SESSION",
  UNKNOWN_MISSION_VERSION: "UNKNOWN_MISSION_VERSION",
  DRAFT_DISCARDED: "DRAFT_DISCARDED",
  MISSION_REBASE_REQUIRED: "MISSION_REBASE_REQUIRED",
  EVOLUTION_SESSION_CLOSED: "EVOLUTION_SESSION_CLOSED",
  INVALID_MISSION_DRAFT: "INVALID_MISSION_DRAFT",
  INTERNAL_ERROR: "INTERNAL_ERROR",
};

/** The error's `payload.code`, or null (network failure / unknown shape). */
export const missionErrorCode = (err) =>
  err?.response?.data?.payload?.code ?? err?.response?.data?.detail?.code ?? null;

/** Where the member started an evolution from (`entry_source`). */
export const MISSION_ENTRY_SOURCE = {
  JOURNEY_HOME: "JOURNEY_HOME",
  JOURNEY_MISSION: "JOURNEY_MISSION",
  AGS_RECOMMENDATION: "AGS_RECOMMENDATION",
  CONVERSATION: "CONVERSATION",
  CHECK_IN: "CHECK_IN",
};

/**
 * `POST /ARIS/v4/journey/mission/evolution` — start or resume (one open
 * evolution per member; a second call answers `resumed: true`). Call only when
 * the member picks Voice or Text. → `{evolution_session_id, resumed, mode,
 * status, draft_id, launch: {voice: {path, query}, text: {path, module, payload}}}`.
 */
export const startMissionEvolution = async ({ mode, entrySource, recommendationId = null }) => {
  const res = await axiosArisChatInstance.post("/ARIS/v4/journey/mission/evolution", {
    mode,
    entry_source: entrySource,
    recommendation_id: recommendationId || null,
  });
  return unwrap(res);
};

/** `POST …/evolution/{id}/cancel` — explicit "stop evolving"; discards any draft. */
export const cancelMissionEvolution = async (evolutionSessionId) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/journey/mission/evolution/${encodeURIComponent(evolutionSessionId)}/cancel`
  );
  return unwrap(res);
};

/** `GET …/mission/drafts/{id}` — proposed vs current, `changed` / `unchanged`, `change_summary`. */
export const getMissionDraft = async (draftId) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/journey/mission/drafts/${encodeURIComponent(draftId)}`
  );
  return unwrap(res);
};

/** `POST …/drafts/{id}/confirm` — "Save as My New Mission". Idempotent (`already_confirmed`). */
export const confirmMissionDraft = async (draftId) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/journey/mission/drafts/${encodeURIComponent(draftId)}/confirm`
  );
  return unwrap(res);
};

/** `POST …/drafts/{id}/discard` — "Keep Current Mission". Idempotent. */
export const discardMissionDraft = async (draftId) => {
  const res = await axiosArisChatInstance.post(
    `/ARIS/v4/journey/mission/drafts/${encodeURIComponent(draftId)}/discard`
  );
  return unwrap(res);
};

/** `GET …/mission/history` → `{items: [{mission_id, label, date_kind, date, statement}]}`, newest first. */
export const getMissionHistory = async () => {
  const res = await axiosArisChatInstance.get("/ARIS/v4/journey/mission/history");
  return unwrap(res);
};

/** `GET …/mission/history/{mission_id}` — one history item plus its `dimensions`, read-only. */
export const getMissionHistoryItem = async (missionId) => {
  const res = await axiosArisChatInstance.get(
    `/ARIS/v4/journey/mission/history/${encodeURIComponent(missionId)}`
  );
  return unwrap(res);
};
